import React from 'react';
import { motion } from 'framer-motion';
import { Check, X, Home, Briefcase } from 'lucide-react';
import { contentVariants } from '../utils/animations';
import useComparisonStore from '../store/comparisonStore';
import { trackEvent } from '../../../config/clarity';

/**
 * OperatorSelector component
 * Allows users to select network operators for comparison
 */
const OperatorSelector = ({ operators = [] }) => {
  const {
    selectedOperators,
    setSelectedOperators,
    selectAllOperators
  } = useComparisonStore();

  // Group operators by type (Home vs Business)
  const homeOperators = operators.filter(op =>
    !op.connectionType.toLowerCase().includes('business') &&
    !op.connectionType.toLowerCase().includes('fttb')
  );
  
  const businessOperators = operators.filter(op =>
    op.connectionType.toLowerCase().includes('business') ||
    op.connectionType.toLowerCase().includes('fttb')
  );

  const toggleOperator = (operatorIdentifier) => {
    console.log(`Toggling operator: ${operatorIdentifier}`);
    let newSelectedOperators;
    
    if (selectedOperators.includes(operatorIdentifier)) {
      // Don't allow deselecting the last operator
      if (selectedOperators.length > 1) {
        // Track operator deselection
        trackEvent('operator_deselected', {
          operator: operatorIdentifier,
          remainingOperatorsCount: selectedOperators.length - 1
        });
        
        newSelectedOperators = selectedOperators.filter(id => id !== operatorIdentifier);
        setSelectedOperators(newSelectedOperators);
      } else {
        // Track attempted deselection of last operator
        trackEvent('last_operator_deselection_prevented', {
          operator: operatorIdentifier
        });
        
        // No change if trying to deselect the last operator
        return;
      }
    } else {
      // Track operator selection
      trackEvent('operator_selected', {
        operator: operatorIdentifier,
        newOperatorsCount: selectedOperators.length + 1
      });
      
      newSelectedOperators = [...selectedOperators, operatorIdentifier];
      setSelectedOperators(newSelectedOperators);
    }
    
    // We no longer automatically open the comparison modal here
    // This is now handled by the parent component
  };
  
  // Select all operators
  const selectAll = () => {
    console.log('Selecting all operators');
    
    // Track select all operators action
    trackEvent('select_all_operators', {
      operatorsCount: operators.length,
      previouslySelectedCount: selectedOperators.length
    });
    
    selectAllOperators();
    
    // We no longer automatically open the comparison modal here
    // This is now handled by the parent component
  };
  
  // Clear all selected operators except one
  const clearAll = () => {
    // Track clear operators action
    trackEvent('clear_operators', {
      previouslySelectedCount: selectedOperators.length
    });
    
    // Keep the first operator selected to avoid empty selection
    if (operators.length > 0) {
      const firstOperator = operators[0];
      const firstOperatorIdentifier = `${firstOperator.operatorName}:${firstOperator.connectionType || 'FTTH'}`;
      
      // Track which operator was kept
      trackEvent('operator_kept_after_clear', {
        operator: firstOperatorIdentifier
      });
      
      setSelectedOperators([firstOperatorIdentifier]);
    }
  };

  return (
    <motion.div 
      className="space-y-4"
      variants={contentVariants}
    >
      <h3 className="text-sm font-medium text-white">Select Network Operators</h3>
      
      {/* Select All / Clear buttons */}
      <div className="flex justify-between mb-3">
        <motion.button
          className="px-3 py-1 text-xs font-medium text-purple-400 hover:text-purple-300 flex items-center"
          onClick={selectAll}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Check className="h-3 w-3 mr-1" />
          Select All
        </motion.button>
        
        <motion.button
          className="px-3 py-1 text-xs font-medium text-gray-400 hover:text-gray-300 flex items-center"
          onClick={clearAll}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <X className="h-3 w-3 mr-1" />
          Clear
        </motion.button>
      </div>
      
      {/* Home Operators */}
      {homeOperators.length > 0 && (
        <div className="mb-3">
          <h4 className="text-xs font-medium text-gray-400 mb-2 flex items-center">
            <Home className="h-3 w-3 mr-1" />
            Home Networks
          </h4>
          <div className="flex flex-wrap gap-2">
            {homeOperators.map((operator, index) => {
              const operatorIdentifier = `${operator.operatorName}:${operator.connectionType || 'FTTH'}`;
              const isSelected = selectedOperators.includes(operatorIdentifier);
              const displayName = operator.displayName || operator.operatorName;
              
              return (
                <motion.button
                  key={index}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center space-x-1 transition-colors ${
                    isSelected
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                  onClick={() => toggleOperator(operatorIdentifier)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {operator.logo && (
                    <img
                      src={operator.logo}
                      alt={operator.operatorName}
                      className="h-4 w-4 object-contain mr-1.5"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <span>{displayName}</span>
                  {isSelected && <Check className="h-3 w-3 ml-1" />}
                </motion.button>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Business Operators */}
      {businessOperators.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-gray-400 mb-2 flex items-center">
            <Briefcase className="h-3 w-3 mr-1" />
            Business Networks
          </h4>
          <div className="flex flex-wrap gap-2">
            {businessOperators.map((operator, index) => {
              const operatorIdentifier = `${operator.operatorName}:${operator.connectionType || 'FTTB'}`;
              const isSelected = selectedOperators.includes(operatorIdentifier);
              const displayName = operator.displayName || operator.operatorName;
              
              // Skip Frogfoot Business as we don't have packages for it
              if (operator.operatorName.toLowerCase().includes('frogfoot') &&
                  (operator.connectionType === 'FTTB' || operator.networkType?.toLowerCase().includes('business'))) {
                return null;
              }
              
              return (
                <motion.button
                  key={index}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center space-x-1 transition-colors ${
                    isSelected
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                  onClick={() => toggleOperator(operatorIdentifier)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {operator.logo && (
                    <img
                      src={operator.logo}
                      alt={operator.operatorName}
                      className="h-4 w-4 object-contain mr-1.5"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <span>{displayName}</span>
                  {isSelected && <Check className="h-3 w-3 ml-1" />}
                </motion.button>
              );
            })}
          </div>
        </div>
      )}
      
      {selectedOperators.length > 0 && (
        <div className="text-xs text-gray-400">
          {selectedOperators.length} operator{selectedOperators.length !== 1 ? 's' : ''} selected
        </div>
      )}
    </motion.div>
  );
};

export default OperatorSelector;